import React, { useCallback, useMemo } from 'react';
import { Select, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import { DuplicateCardsHelpers } from 'ontraccr-common';

import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import { toTitleCase } from '../../../helpers/helpers';

const {
  RELATIVE_DATE_ANCHORS,
  RELATIVE_DATE_DIRECTIONS,
  RELATIVE_DATE_UNITS,
  getRelativeDateValueLabel,
} = DuplicateCardsHelpers;

const { Text } = Typography;

export const DEFAULT_RELATIVE_DATE = {
  amount: 1,
  unit: 'months',
  direction: 'before',
  anchor: RELATIVE_DATE_ANCHORS.TODAY,
};

export const RELATIVE_DATE_UNIT_OPTIONS = RELATIVE_DATE_UNITS.map((unit) => ({
  value: unit,
  label: toTitleCase(unit),
}));

export const RELATIVE_DATE_DIRECTION_OPTIONS = RELATIVE_DATE_DIRECTIONS.map((direction) => ({
  value: direction,
  label: direction,
}));

export const getRelativeDateAnchorLabel = (anchor, fieldLabel) => (
  anchor === RELATIVE_DATE_ANCHORS.FIELD ? `${fieldLabel} on this form` : 'Today'
);

// The field anchor is only offered when the row's own card field is mapped from the form
export const getRelativeDateAnchorOptions = ({ fieldLabel, hasFieldAnchor }) => [
  { value: RELATIVE_DATE_ANCHORS.TODAY, label: 'Today' },
  ...(hasFieldAnchor ? [{
    value: RELATIVE_DATE_ANCHORS.FIELD,
    label: getRelativeDateAnchorLabel(RELATIVE_DATE_ANCHORS.FIELD, fieldLabel),
  }] : []),
];

export default function RelativeDateInput({
  operator,
  value,
  evaluatedValue,
  fieldLabel,
  hasFieldAnchor,
  anchorResponse,
  onChange,
}) {
  const isFieldAnchor = value.anchor === RELATIVE_DATE_ANCHORS.FIELD;

  const anchorOptions = useMemo(() => (
    getRelativeDateAnchorOptions({ fieldLabel, hasFieldAnchor: hasFieldAnchor || isFieldAnchor })
  ), [fieldLabel, hasFieldAnchor, isFieldAnchor]);

  const onPartChange = useCallback((key) => (newPart) => {
    onChange({ ...value, [key]: newPart });
  }, [onChange, value]);

  const description = useMemo(() => {
    const label = getRelativeDateValueLabel({
      operator,
      value: evaluatedValue,
      timezone: isFieldAnchor ? anchorResponse?.timezone : undefined,
    });
    if (label) return { text: `Evaluates to: ${label}` };
    if (!operator) return { text: 'Select an operator to evaluate this date' };
    if (!Number.isInteger(value.amount) || value.amount < 1) {
      return { text: 'Enter a whole number of at least 1', type: 'danger' };
    }
    if (isFieldAnchor) return { text: `${fieldLabel} is empty on this form`, type: 'warning' };
    return null;
  }, [operator, evaluatedValue, isFieldAnchor, anchorResponse, value.amount, fieldLabel]);

  return (
    <Space direction="vertical" size={4}>
      <Space size={8}>
        <OnTraccrNumberInput
          min={1}
          precision={0}
          style={{ width: 70, display: 'block' }}
          value={value.amount}
          onChange={onPartChange('amount')}
        />
        <Select
          style={{ width: 100 }}
          options={RELATIVE_DATE_UNIT_OPTIONS}
          value={value.unit}
          onChange={onPartChange('unit')}
        />
        <Select
          style={{ width: 90 }}
          options={RELATIVE_DATE_DIRECTION_OPTIONS}
          value={value.direction}
          onChange={onPartChange('direction')}
        />
        <Select
          style={{ width: 260 }}
          dropdownMatchSelectWidth={false}
          options={anchorOptions}
          value={value.anchor}
          onChange={onPartChange('anchor')}
        />
      </Space>
      {description && <Text type={description.type ?? 'secondary'}>{description.text}</Text>}
    </Space>
  );
}

RelativeDateInput.propTypes = {
  operator: PropTypes.string,
  value: PropTypes.shape({
    amount: PropTypes.number,
    unit: PropTypes.string,
    direction: PropTypes.string,
    anchor: PropTypes.string,
  }).isRequired,
  evaluatedValue: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  fieldLabel: PropTypes.string,
  hasFieldAnchor: PropTypes.bool,
  anchorResponse: PropTypes.shape({
    date: PropTypes.number,
    timezone: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
};

RelativeDateInput.defaultProps = {
  operator: null,
  evaluatedValue: null,
  fieldLabel: '',
  hasFieldAnchor: false,
  anchorResponse: null,
};
